export interface ApiClientConfig {
  baseUrl: string;
  token?: string | null;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export class ApiClient {
  private baseUrl: string;
  private token: string | null;
  private csrfToken: string | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.token = config.token ?? null;
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  private async ensureCsrfToken(): Promise<string> {
    if (this.csrfToken) {
      return this.csrfToken;
    }

    const cookieMatch = document.cookie.match(/csrf-token=([^;]+)/);
    if (cookieMatch?.[1]) {
      this.csrfToken = cookieMatch[1];
      return this.csrfToken;
    }

    try {
      const response = await fetch(`${this.baseUrl}/csrf-token`, {
        credentials: 'include',
      });
      const data = (await response.json()) as { csrfToken?: string };
      if (data.csrfToken) {
        this.csrfToken = data.csrfToken;
        return this.csrfToken;
      }
    } catch {
      // Ignore
    }

    return '';
  }

  private async getHeaders(method: string): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
      console.log('header ',method,this);
    }

    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrf = await this.ensureCsrfToken();
      if (csrf) {
        headers['X-CSRF-Token'] = csrf;
      }
    }

    return headers;
  }

  async request<T>(
    method: string,
    path: string,
    data?: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method,
      headers: await this.getHeaders(method),
      credentials: 'include',
    };

    if (data && method !== 'GET') {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(`${this.baseUrl}${path}`, options);
      return response.json() as Promise<ApiResponse<T>>;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async get<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, data?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, data);
  }

  async put<T>(path: string, data?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, data);
  }

  async delete<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path);
  }

  async call1C<T>(method: string, params: Record<string, unknown>): Promise<ApiResponse<T>> {
    return this.post<T>(`/api/gateway/1c/${method}`, params);
  }
}

let apiInstance: ApiClient | null = null;

export function getApi(): ApiClient {
  if (!apiInstance) {
    const token = typeof document !== 'undefined'
      ? (document.cookie.match(/token=([^;]+)/)?.[1] ?? null)
      : null;

    apiInstance = new ApiClient({
      baseUrl: typeof window !== 'undefined' ? window.API_URL : '',
      token,
    });
  }
  return apiInstance;
}

export const api = getApi();
